import { FFmpeg, FFFSType } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import { buildClipArgs, buildMusicArgs, FONT_FILE, MUSIC_FILE } from "./ffmpegArgs";

/**
 * Single shared FFmpeg instance for the whole app.
 *
 * Source videos are never copied into the WASM heap: they are mounted with
 * WORKERFS so FFmpeg reads straight from the browser File object. Only the
 * outputs (clips, concat result, final video) live in the in-memory FS.
 */

const CORE_VERSION = "0.12.10";
const MOUNT_POINT = "/input";

const ffmpeg = new FFmpeg();

// Mutable per-run listeners so a single instance can serve many callers.
let progressListener = null;
let logListener = null;
const recentLogs = [];

ffmpeg.on("log", ({ message }) => {
  recentLogs.push(message);
  if (recentLogs.length > 40) recentLogs.shift();
  if (logListener) logListener(message);
});

ffmpeg.on("progress", (event) => {
  if (progressListener) progressListener(event);
});

const absoluteUrl = (path) =>
  new URL(`${process.env.PUBLIC_URL || ""}${path}`, document.baseURI).href;

let loadPromise = null;

/**
 * Load the FFmpeg core (once). Safe to call concurrently.
 */
const loadFFmpeg = () => {
  if (ffmpeg.loaded) return Promise.resolve();
  if (!loadPromise) {
    loadPromise = ffmpeg
      .load({
        coreURL: absoluteUrl(`/ffmpeg/core@${CORE_VERSION}/ffmpeg-core.js`),
        wasmURL: absoluteUrl(`/ffmpeg/core@${CORE_VERSION}/ffmpeg-core.wasm`),
      })
      .catch((error) => {
        loadPromise = null;
        throw error;
      });
  }
  return loadPromise;
};

/**
 * Run an ffmpeg command. Throws on a non-zero exit code.
 * @param {string[]} args
 * @param {{ expectedSeconds?: number, onProgress?: (fraction: number) => void }} [options]
 */
const run = async (args, { expectedSeconds, onProgress } = {}) => {
  recentLogs.length = 0;
  let seenEvents = 0;
  progressListener =
    onProgress && expectedSeconds > 0
      ? ({ time }) => {
          // The first event of a run can carry a stale timestamp; skip it.
          if (seenEvents++ === 0) return;
          // `time` is the processed output timestamp in microseconds.
          const fraction = time / (expectedSeconds * 1e6);
          if (Number.isFinite(fraction)) onProgress(Math.max(0, Math.min(1, fraction)));
        }
      : null;

  let exitCode;
  try {
    exitCode = await ffmpeg.exec(args);
  } finally {
    progressListener = null;
  }

  if (exitCode !== 0) {
    const tail = recentLogs.slice(-6).join("\n");
    throw new Error(`FFmpeg failed (exit ${exitCode}).\n${tail}`);
  }
  if (onProgress) onProgress(1);
};

/**
 * Probe a file for duration and stream layout.
 */
const probe = async (path) => {
  const outName = `probe-${Date.now()}.json`;
  // The core's ffprobe does not report a meaningful exit code, so success is
  // judged by whether it produced parseable output.
  recentLogs.length = 0;
  await ffmpeg.ffprobe([
    "-v", "error",
    "-show_entries", "stream=codec_type:format=duration",
    "-of", "json",
    path,
    "-o", outName,
  ]);

  let info;
  try {
    const text = await ffmpeg.readFile(outName, "utf8");
    await ffmpeg.deleteFile(outName);
    info = JSON.parse(text);
  } catch (e) {
    const tail = recentLogs.filter((line) => line !== "Aborted()").slice(-4).join("\n");
    throw new Error(`Could not read the video file.${tail ? `\n${tail}` : ""}`);
  }
  const streams = info.streams || [];
  return {
    duration: parseFloat(info.format?.duration) || 0,
    hasVideo: streams.some((s) => s.codec_type === "video"),
    hasAudio: streams.some((s) => s.codec_type === "audio"),
  };
};

const deleteQuietly = async (name) => {
  try {
    await ffmpeg.deleteFile(name);
  } catch (e) {
    // File may not exist; nothing to clean up.
  }
};

const fileExtension = (name) => {
  const match = /\.[A-Za-z0-9]+$/.exec(name || "");
  return match ? match[0].toLowerCase() : ".mp4";
};

/**
 * Mount browser Files/Blobs under MOUNT_POINT, run `fn`, then unmount.
 * @param {{ name: string, data: Blob }[]} entries
 * @param {(paths: string[]) => Promise<T>} fn
 */
const withMountedFiles = async (entries, fn) => {
  try {
    await ffmpeg.createDir(MOUNT_POINT);
  } catch (e) {
    // Directory already exists from an earlier aborted run.
  }
  await ffmpeg.mount(FFFSType.WORKERFS, { blobs: entries }, MOUNT_POINT);
  try {
    return await fn(entries.map((entry) => `${MOUNT_POINT}/${entry.name}`));
  } finally {
    try {
      await ffmpeg.unmount(MOUNT_POINT);
      await ffmpeg.deleteDir(MOUNT_POINT);
    } catch (e) {
      console.warn("Could not unmount input files:", e);
    }
  }
};

/**
 * Trigger a browser download for a Blob.
 */
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const timestamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const writeMusicFile = async (selectedMusic) => {
  const { track } = selectedMusic;
  if (track.isLocal && track.file) {
    await ffmpeg.writeFile(MUSIC_FILE, await fetchFile(track.file));
    return;
  }
  const source = track.file || track.localPath || track.preview_url;
  const response = await fetch(source, { mode: "cors", credentials: "omit" });
  if (!response.ok) throw new Error("Could not download the music track.");
  await ffmpeg.writeFile(MUSIC_FILE, new Uint8Array(await response.arrayBuffer()));
};

/**
 * Generate the highlight compilation.
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
const generateHighlightVideo = async ({
  highlights,
  videoFile,
  selectedMusic,
  preset = "fast",
  onProgress = () => {},
  onStep = () => {},
}) => {
  if (!highlights.length || !videoFile) {
    throw new Error("Nothing to generate.");
  }

  // Progress never moves backwards, whatever order FFmpeg events arrive in.
  let reported = 0;
  const report = (value) => {
    reported = Math.max(reported, Math.min(1, value));
    onProgress(reported);
  };

  report(0);
  onStep("Loading FFmpeg...");
  await loadFFmpeg();
  report(0.05);

  onStep("Loading resources...");
  const fontData = await fetchFile(absoluteUrl(`/${FONT_FILE}`));
  await ffmpeg.writeFile(FONT_FILE, fontData);

  let musicReady = false;
  if (selectedMusic) {
    onStep("Preparing music...");
    try {
      await writeMusicFile(selectedMusic);
      musicReady = true;
    } catch (error) {
      console.error("Error preparing music:", error);
      onStep("Could not load music. Continuing without it...");
    }
  }
  report(0.1);

  const sorted = [...highlights].sort((a, b) => a.startTime - b.startTime);
  const clipNames = [];
  const sourceName = `source${fileExtension(videoFile.name)}`;

  try {
    return await withMountedFiles([{ name: sourceName, data: videoFile }], async ([inputPath]) => {
      const source = await probe(inputPath);
      if (!source.hasVideo) throw new Error("The selected file has no video stream.");

      // Each clip owns an equal share of the 0.1 .. 0.8 range.
      const clipShare = 0.7 / sorted.length;

      for (let i = 0; i < sorted.length; i++) {
        const highlight = sorted[i];
        onStep(`Processing highlight ${i + 1} of ${sorted.length}...`);

        const outputName = `highlight-${i}.mp4`;
        let textFile = null;
        if (highlight.text && highlight.text.trim()) {
          textFile = `text-${i}.txt`;
          await ffmpeg.writeFile(textFile, highlight.text);
        }

        const outputSeconds = highlight.duration * (highlight.slowMotion ? 2 : 1);
        const args = buildClipArgs({
          inputPath,
          outputName,
          startTime: highlight.startTime,
          duration: highlight.duration,
          slowMotion: highlight.slowMotion,
          zoom: highlight.zoom,
          textFile,
          color: highlight.color,
          preset,
          hasAudio: source.hasAudio,
        });

        await run(args, {
          expectedSeconds: outputSeconds,
          onProgress: (fraction) => report(0.1 + clipShare * (i + fraction)),
        });

        if (textFile) await deleteQuietly(textFile);
        clipNames.push(outputName);
      }

      onStep("Combining video segments...");
      const listText = clipNames.map((name) => `file '${name}'`).join("\n");
      await ffmpeg.writeFile("filelist.txt", listText);
      await run(["-f", "concat", "-safe", "0", "-i", "filelist.txt", "-c", "copy", "combined.mp4"]);
      report(0.85);

      let finalName = "combined.mp4";
      if (musicReady) {
        onStep("Adding music to video...");
        const combined = await probe("combined.mp4");
        try {
          await run(
            buildMusicArgs({
              inputName: "combined.mp4",
              outputName: "output.mp4",
              music: selectedMusic,
              totalDuration: combined.duration,
              videoHasAudio: combined.hasAudio,
            }),
            {
              expectedSeconds: combined.duration,
              onProgress: (fraction) => report(0.85 + 0.1 * fraction),
            }
          );
          finalName = "output.mp4";
        } catch (error) {
          console.error("Error adding music:", error);
          onStep("Error adding music. Continuing without music...");
        }
      }
      report(0.95);

      onStep("Finalizing video...");
      const data = await ffmpeg.readFile(finalName);
      const blob = new Blob([data.buffer], { type: "video/mp4" });

      report(1);
      onStep("Complete!");
      return { blob, filename: `highlights_${timestamp()}.mp4` };
    });
  } finally {
    await Promise.all([
      ...clipNames.map(deleteQuietly),
      deleteQuietly("filelist.txt"),
      deleteQuietly("combined.mp4"),
      deleteQuietly("output.mp4"),
      deleteQuietly(MUSIC_FILE),
      deleteQuietly(FONT_FILE),
    ]);
  }
};

/**
 * Split a video into time-based segments without re-encoding.
 * Each segment is produced in its own pass so only one segment at a time
 * occupies memory; `onSegment` receives it as a Blob.
 * @returns {Promise<number>} number of segments produced
 */
const splitVideo = async ({
  file,
  segmentSeconds,
  onProgress = () => {},
  onLog = () => {},
  onSegment = () => {},
}) => {
  await loadFFmpeg();
  const sourceName = `source${fileExtension(file.name)}`;

  return withMountedFiles([{ name: sourceName, data: file }], async ([inputPath]) => {
    const { duration } = await probe(inputPath);
    if (!duration) throw new Error("Could not determine the video duration.");

    const length = Math.max(1, Number(segmentSeconds) || 1);
    const total = Math.max(1, Math.ceil(duration / length));
    onLog(`Splitting ${duration.toFixed(1)}s of video into ${total} segments of ~${length.toFixed(0)}s`);

    for (let i = 0; i < total; i++) {
      const start = i * length;
      const span = Math.min(length, duration - start);
      const outputName = `segment-${i}.mp4`;
      onLog(`Processing segment ${i + 1}/${total} (${start.toFixed(0)}s to ${(start + span).toFixed(0)}s)`);

      await run(
        [
          "-ss", String(start),
          "-t", String(span),
          "-i", inputPath,
          "-map", "0:v:0",
          "-map", "0:a:0?",
          "-c", "copy",
          "-avoid_negative_ts", "make_zero",
          "-movflags", "+faststart",
          outputName,
        ],
        {
          expectedSeconds: span,
          onProgress: (fraction) => onProgress((i + fraction) / total),
        }
      );

      const data = await ffmpeg.readFile(outputName);
      await deleteQuietly(outputName);
      const blob = new Blob([data.buffer], { type: "video/mp4" });
      onLog(`Segment ${i + 1} size: ${(blob.size / (1024 * 1024)).toFixed(2)}MB`);
      onSegment({ index: i, total, blob });
      onProgress((i + 1) / total);
    }

    return total;
  });
};

/**
 * Concatenate several videos (same codec/resolution) without re-encoding.
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
const mergeVideos = async (files, { onProgress = () => {} } = {}) => {
  if (!files.length) throw new Error("No files to merge.");
  await loadFFmpeg();

  const entries = files.map((file, i) => ({
    name: `input-${i}${fileExtension(file.name)}`,
    data: file,
  }));

  try {
    return await withMountedFiles(entries, async (paths) => {
      let totalDuration = 0;
      for (const path of paths) {
        totalDuration += (await probe(path)).duration;
      }

      const listText = paths.map((path) => `file '${path}'`).join("\n");
      await ffmpeg.writeFile("merge-list.txt", listText);
      await run(
        ["-f", "concat", "-safe", "0", "-i", "merge-list.txt", "-c", "copy", "-movflags", "+faststart", "merged.mp4"],
        { expectedSeconds: totalDuration, onProgress }
      );

      const data = await ffmpeg.readFile("merged.mp4");
      return {
        blob: new Blob([data.buffer], { type: "video/mp4" }),
        filename: `merged_highlights_${timestamp()}.mp4`,
      };
    });
  } finally {
    await Promise.all([deleteQuietly("merge-list.txt"), deleteQuietly("merged.mp4")]);
  }
};

const setLogListener = (listener) => {
  logListener = listener;
};

export {
  ffmpeg,
  loadFFmpeg,
  generateHighlightVideo,
  splitVideo,
  mergeVideos,
  downloadBlob,
  setLogListener,
};
